import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useCreateTier } from "./use-create-tier";
import { useUpdateTier } from "./use-update-tier";
import {
  MAX_TIER_DAILY_PASS_CAP,
  MAX_TIER_WEIGHT,
  MIN_TIER_DAILY_PASS_CAP,
  MIN_TIER_WEIGHT,
  TIER_DISPLAY_NAME_MAX_LENGTH,
  TIER_KEY_MAX_LENGTH,
} from "./constants";
import {
  canDeactivate,
  isDailyPassCapValid,
  isDisplayNameValid,
  isKeyUnique,
  isProductLinkUnique,
  isTierKeyValid,
  isWeightUnique,
  isWeightValid,
} from "./tier-validation";
import type {
  CatalogProductOption,
  CreateTierInput,
  SubscriptionTierConfig,
  UpdateTierInput,
} from "./types";

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
const LABEL_CLASS = "font-sans text-sm font-medium text-foreground";
const HINT_CLASS = "font-sans text-xs text-muted-foreground";
const ERROR_HINT_CLASS = "font-sans text-xs text-destructive";

/** Shared props; `mode` discriminates whether `tier` is required. */
interface TierFormDialogCommonProps {
  /** All tiers — for client-side uniqueness + last-active checks. */
  readonly tiers: readonly SubscriptionTierConfig[];
  readonly products: readonly CatalogProductOption[];
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

type TierFormDialogProps =
  | ({ readonly mode: "create"; readonly tier?: never } & TierFormDialogCommonProps)
  | ({ readonly mode: "edit"; readonly tier: SubscriptionTierConfig } & TierFormDialogCommonProps);

function parseIntField(raw: string): number {
  const trimmed = raw.trim();
  return trimmed === "" ? Number.NaN : Number(trimmed);
}

/**
 * Next spaced weight (max + 100) so a created tier slots after the rest.
 * Returns null when the gap would exceed the max, so the field opens blank and
 * the operator is guided by validation instead of a pre-broken value.
 */
function suggestWeight(tiers: readonly SubscriptionTierConfig[]): number | null {
  if (tiers.length === 0) return 100;
  const next = Math.max(...tiers.map((t) => t.weight)) + 100;
  return next <= MAX_TIER_WEIGHT ? next : null;
}

export function TierFormDialog({
  mode,
  tier,
  tiers,
  products,
  open,
  onOpenChange,
  onSuccess,
}: TierFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = mode === "edit";
  const excludeId = tier?.id;

  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [weight, setWeight] = useState("");
  const [dailyPassCap, setDailyPassCap] = useState("");
  const [stripeProductId, setStripeProductId] = useState("");
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateTier();
  const update = useUpdateTier(tier?.id ?? "");
  const pending = create.isPending || update.isPending;

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (isEdit && tier) {
      setKey(tier.key);
      setDisplayName(tier.displayName);
      setWeight(String(tier.weight));
      setDailyPassCap(String(tier.dailyPassCap));
      setStripeProductId(tier.stripeProductId ?? "");
      setActive(tier.active);
    } else {
      setKey("");
      setDisplayName("");
      const suggested = suggestWeight(tiers);
      setWeight(suggested !== null ? String(suggested) : "");
      setDailyPassCap("");
      setStripeProductId("");
      setActive(true);
    }
    // `tiers` intentionally omitted: the weight suggestion is a one-time init on
    // open. Including it would reset all unsaved fields on a background refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, tier]);

  const weightNum = parseIntField(weight);
  const capNum = parseIntField(dailyPassCap);
  const linkedProductId = stripeProductId === "" ? null : stripeProductId;

  // Field-level validity (client mirror of the API invariants).
  const keyValid = isTierKeyValid(key);
  const keyUnique = isKeyUnique(key, tiers, excludeId);
  const nameValid = isDisplayNameValid(displayName);
  const weightValid = isWeightValid(weightNum);
  const weightUnique = isWeightUnique(weightNum, tiers, excludeId);
  const capValid = isDailyPassCapValid(capNum);
  const productUnique = isProductLinkUnique(linkedProductId, tiers, excludeId);
  // Deactivating the last active tier is forbidden (LAST_ACTIVE_TIER).
  const deactivationBlocked = isEdit && tier != null && !active && !canDeactivate(tier, tiers);

  const keyOk = isEdit || (keyValid && keyUnique);
  const weightOk = weightValid && weightUnique;

  const formValid =
    keyOk && nameValid && weightOk && capValid && productUnique && !deactivationBlocked;

  const buildCreateInput = (): CreateTierInput => ({
    key: key.trim(),
    displayName: displayName.trim(),
    weight: weightNum,
    dailyPassCap: capNum,
    stripeProductId: linkedProductId,
    active,
  });

  // Edit: send only changed fields (the API rejects an empty patch as 400).
  const buildUpdateInput = (): UpdateTierInput => {
    if (!tier) return {};
    return {
      ...(displayName.trim() !== tier.displayName ? { displayName: displayName.trim() } : {}),
      ...(weightNum !== tier.weight ? { weight: weightNum } : {}),
      ...(capNum !== tier.dailyPassCap ? { dailyPassCap: capNum } : {}),
      ...(linkedProductId !== tier.stripeProductId ? { stripeProductId: linkedProductId } : {}),
      ...(active !== tier.active ? { active } : {}),
    };
  };

  const updatePatch = isEdit ? buildUpdateInput() : null;
  const hasChanges = !isEdit || Object.keys(updatePatch ?? {}).length > 0;
  const canSubmit = formValid && hasChanges && !pending;

  const handleClose = (next: boolean) => {
    if (!next && pending) return;
    onOpenChange(next);
  };

  const onError = (err: unknown) => {
    setError(
      resolveMutationErrorMessage(err, t, {
        forbiddenKey: "subscriptionTiers.errors.forbidden",
        serverPrefixKey: "subscriptionTiers.errors.serverPrefix",
        genericKey: "subscriptionTiers.errors.generic",
      }),
    );
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    setError(null);
    if (isEdit) {
      update.mutate(buildUpdateInput(), {
        onSuccess: () => onSuccess(),
        onError,
      });
    } else {
      create.mutate(buildCreateInput(), {
        onSuccess: () => onSuccess(),
        onError,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="tier-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("subscriptionTiers.form.editTitle")
              : t("subscriptionTiers.form.createTitle")}
          </DialogTitle>
          <DialogDescription>{t("subscriptionTiers.form.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <label htmlFor="tier-key" className={LABEL_CLASS}>
              {t("subscriptionTiers.fields.key")}
            </label>
            <Input
              id="tier-key"
              data-testid="tier-key-input"
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase())}
              maxLength={TIER_KEY_MAX_LENGTH}
              disabled={isEdit || pending}
              aria-invalid={!isEdit && !keyOk}
              autoComplete="off"
            />
            <p className={!isEdit && !keyOk ? ERROR_HINT_CLASS : HINT_CLASS}>
              {isEdit
                ? t("subscriptionTiers.hints.keyImmutable")
                : t("subscriptionTiers.hints.key")}
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="tier-display-name" className={LABEL_CLASS}>
              {t("subscriptionTiers.fields.displayName")}
            </label>
            <Input
              id="tier-display-name"
              data-testid="tier-displayname-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={TIER_DISPLAY_NAME_MAX_LENGTH}
              disabled={pending}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor="tier-weight" className={LABEL_CLASS}>
                {t("subscriptionTiers.fields.weight")}
              </label>
              <Input
                id="tier-weight"
                data-testid="tier-weight-input"
                type="number"
                inputMode="numeric"
                min={MIN_TIER_WEIGHT}
                max={MAX_TIER_WEIGHT}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                disabled={pending}
                aria-invalid={!weightOk}
              />
              <p className={!weightOk ? ERROR_HINT_CLASS : HINT_CLASS}>
                {weightValid && !weightUnique
                  ? t("subscriptionTiers.hints.weightTaken")
                  : t("subscriptionTiers.hints.weight")}
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="tier-cap" className={LABEL_CLASS}>
                {t("subscriptionTiers.fields.dailyPassCap")}
              </label>
              <Input
                id="tier-cap"
                data-testid="tier-dailypasscap-input"
                type="number"
                inputMode="numeric"
                min={MIN_TIER_DAILY_PASS_CAP}
                max={MAX_TIER_DAILY_PASS_CAP}
                value={dailyPassCap}
                onChange={(e) => setDailyPassCap(e.target.value)}
                disabled={pending}
                aria-invalid={!capValid}
              />
              <p className={!capValid ? ERROR_HINT_CLASS : HINT_CLASS}>
                {t("subscriptionTiers.hints.dailyPassCap", {
                  min: MIN_TIER_DAILY_PASS_CAP,
                  max: MAX_TIER_DAILY_PASS_CAP,
                })}
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="tier-product" className={LABEL_CLASS}>
              {t("subscriptionTiers.fields.stripeProduct")}
            </label>
            <select
              id="tier-product"
              data-testid="link-product-select"
              value={stripeProductId}
              onChange={(e) => setStripeProductId(e.target.value)}
              className={SELECT_CLASS}
              disabled={pending}
              aria-invalid={!productUnique}
              aria-describedby="tier-product-hint"
            >
              <option value="">{t("subscriptionTiers.fields.noProduct")}</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.active ? "" : ` ${t("subscriptionTiers.fields.inactiveSuffix")}`}
                </option>
              ))}
            </select>
            <p id="tier-product-hint" className={!productUnique ? ERROR_HINT_CLASS : HINT_CLASS}>
              {!productUnique
                ? t("subscriptionTiers.hints.productTaken")
                : t("subscriptionTiers.hints.product")}
            </p>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              data-testid="tier-active-checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              disabled={pending}
              className="h-4 w-4 rounded border-input"
            />
            <span className={LABEL_CLASS}>{t("subscriptionTiers.fields.active")}</span>
          </label>
          {deactivationBlocked && (
            <p data-testid="tier-last-active-error" className={ERROR_HINT_CLASS}>
              {t("subscriptionTiers.hints.lastActive")}
            </p>
          )}

          {error && (
            <p
              data-testid="tier-form-error"
              role="alert"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={pending}>
            {t("subscriptionTiers.form.cancel")}
          </Button>
          <Button data-testid="save-tier-button" onClick={handleSubmit} disabled={!canSubmit}>
            {pending ? t("subscriptionTiers.form.saving") : t("subscriptionTiers.form.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
